import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, X, History } from 'lucide-react';
import { API, fmtHistoryDate } from '../utils';

/**
 * Chronological audit log for a single KB — every grading_instructions edit,
 * regrade run and AI-draft. Fetched lazily when the panel is opened so an
 * ordinary KB list render doesn't spam the backend.
 */
export const KBHistoryPanel = ({ kbId, token, onClose }) => {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/knowledge-bases/${kbId}/history?limit=100`, { headers });
        if (!cancelled) setEvents(r.data?.events || []);
      } catch (err) {
        console.error('KB history fetch failed', err?.response || err);
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kbId, headers]);

  const eventStyle = {
    edit:     { label: 'Edit',     cls: 'bg-teal-500/10 text-teal-300 border-teal-500/20' },
    regrade:  { label: 'Regrade',  cls: 'bg-purple-500/10 text-purple-300 border-purple-500/20' },
    ai_draft: { label: 'AI draft', cls: 'bg-amber-500/10 text-amber-300 border-amber-500/20' },
  };

  return (
    <div className="mt-4 rounded-lg bg-black/40 border border-white/10 p-3 space-y-2" data-testid={`kb-history-panel-${kbId}`}>
      <div className="flex items-center justify-between">
        <p className="text-white/80 text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
          <History className="w-3 h-3" />Change history
        </p>
        <button onClick={onClose} className="text-white/30 hover:text-white/60"><X className="w-3.5 h-3.5" /></button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-white/30 text-xs"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>
      ) : events.length === 0 ? (
        <p className="text-white/25 text-xs py-2">No changes recorded yet. Edits, AI drafts and regrade runs from now on will show up here.</p>
      ) : (
        <div className="space-y-1">
          {events.map(e => {
            const style = eventStyle[e.event] || { label: e.event, cls: 'bg-white/5 text-white/50 border-white/10' };
            const isExpanded = expandedId === e.id;
            const showDiffToggle = e.event === 'edit' && e.before && e.after;
            return (
              <div key={e.id} className="rounded-md bg-white/[0.02] border border-white/5 px-3 py-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wider ${style.cls}`}>{style.label}</span>
                  <span className="text-white/60">{e.actor_name || e.actor_email || 'unknown'}</span>
                  <span className="text-white/25 ml-auto">{fmtHistoryDate(e.timestamp)}</span>
                </div>
                {e.event === 'regrade' && (
                  <p className="text-white/50 mt-1 text-[11px]">
                    Regraded {e.meta?.calls_affected ?? 0} calls · last {e.meta?.days ?? '?'} days · call_type <code className="text-white/70">{e.meta?.call_type}</code>
                  </p>
                )}
                {e.event === 'ai_draft' && (
                  <p className="text-white/50 mt-1 text-[11px]">Generated {e.meta?.draft_length ?? '?'} chars from {(e.meta?.doc_chars ?? 0).toLocaleString()} chars of docs · source <code className="text-white/70">{e.meta?.source}</code></p>
                )}
                {e.event === 'edit' && (
                  <p className="text-white/50 mt-1 text-[11px]">
                    Changed: {(e.meta?.changed_fields || []).map(f => <code key={f} className="mx-0.5 text-white/70">{f}</code>)}
                  </p>
                )}
                {showDiffToggle && (
                  <button onClick={() => setExpandedId(isExpanded ? null : e.id)} className="text-teal-400 hover:text-teal-300 text-[10px] mt-1">
                    {isExpanded ? 'Hide diff' : 'Show diff'}
                  </button>
                )}
                {isExpanded && (
                  <div className="mt-2 space-y-1.5 border-t border-white/5 pt-2">
                    {Object.keys(e.after || {}).map(field => (
                      <div key={field} className="space-y-1">
                        <p className="text-white/40 text-[10px] uppercase tracking-wider">{field}</p>
                        <div className="grid grid-cols-2 gap-2 text-[11px]">
                          <div className="bg-red-500/5 border border-red-500/15 rounded p-1.5">
                            <p className="text-red-300/60 text-[9px] uppercase mb-0.5">before</p>
                            <pre className="text-white/60 whitespace-pre-wrap break-words max-h-40 overflow-y-auto font-sans">{JSON.stringify(e.before?.[field] ?? '', null, 2).slice(0, 1200)}</pre>
                          </div>
                          <div className="bg-emerald-500/5 border border-emerald-500/15 rounded p-1.5">
                            <p className="text-emerald-300/60 text-[9px] uppercase mb-0.5">after</p>
                            <pre className="text-white/60 whitespace-pre-wrap break-words max-h-40 overflow-y-auto font-sans">{JSON.stringify(e.after?.[field] ?? '', null, 2).slice(0, 1200)}</pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
